/**
 * TableDisplay test Adapters to abstract common unit test actions away from the implementation details.
 */
import { render, RenderResult, waitFor } from '@testing-library/preact';
import { expect } from 'chai';
import { VNode } from 'preact';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { matchTranslationBundle } from '../../utils/UNSAFE_matchTranslationBundle';
import { RootEnvironment, RootEnvironmentProvider } from '../../UNSAFE_Environment';

/**
 * Constant for timeout for longer running TableDisplay tests
 */
const TABLEDISPLAY_LONG_TIMEOUT = 20000;
const TABLEDISPLAY_SHORT_TIMEOUT = 5000;

export const getShortTimeout = () => {
  return TABLEDISPLAY_SHORT_TIMEOUT;
};

/**
 * Helper method to render a TableDisplay test component with the proper environment setup
 */
export const setupTableDisplay = async (TestComponent: VNode) => {
  const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
  const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
  const env: Partial<RootEnvironment> = {
    translations: { '@oracle/oraclejet-preact': translations }
  };
  return render(
    <RootEnvironmentProvider environment={env}>{TestComponent}</RootEnvironmentProvider>
  );
};

/**
 * Helper function to get all 'grid' role elements within the given rendered component.
 */
export const getInnerGrids = async (tableDisplay: RenderResult) => {
  return tableDisplay.findAllByRole('table');
};

/**
 * Helper function to get all 'columnheader' role elements within the given rendered component.
 */
export const getColumnHeaders = async (tableDisplay: RenderResult) => {
  return tableDisplay.findAllByRole('columnheader');
};

/**
 * Helper function to get all 'row' role elements within the given rendered component.
 */
export const getRows = async (tableDisplay: RenderResult) => {
  return tableDisplay.findAllByRole('row');
};

/**
 * Helper function to get all data cell elements within the given data row.
 */
export const getDataCells = async (dataRow: Element) => {
  return dataRow.children;
};

/**
 * Helper function to get all footer elements within the given rendered component.
 */
export const getColumnFooters = async (tableDisplay: RenderResult) => {
  const rows = await getRows(tableDisplay);
  if (rows.length > 2) {
    return rows[rows.length - 1].children;
  }
  return [];
};

/**
 * Helper method to find the containing cell element of a given element.
 */
export const findContainingCell = (
  rootElement: HTMLElement,
  contentElement: HTMLElement
): HTMLElement | null => {
  if (contentElement.dataset['ojCellType'] != null) {
    return contentElement;
  }
  const parentElement = contentElement.parentElement;
  if (parentElement === rootElement || parentElement == null) {
    return null;
  }
  return findContainingCell(rootElement, parentElement);
};

/**
 * Helper function to determine if the current environment is a Mac.
 */
export const isMac = () => {
  return getClientHints().platform === 'mac';
};

/**
 * Helper function to get the 'rowKey' test element.
 */
export const getRowKey = () => {
  return document.getElementById('rowKey');
};

/**
 * Helper function to get the 'columnKey' test element.
 */
export const getColumnKey = () => {
  return document.getElementById('columnKey');
};

/**
 * Helper function to get the 'cellType' test element.
 */
export const getCellType = () => {
  return document.getElementById('cellType');
};

/**
 * Helper function to verify the width of a Table element.
 */
export const verifyElementWidth = async (element: HTMLElement, width: number) => {
  await waitFor(
    () => {
      expect(element.offsetWidth).to.equal(width);
    },
    { timeout: TABLEDISPLAY_SHORT_TIMEOUT }
  );
};

/**
 * Helper function to verify the width of a Table element.
 */
export const determineBrowserScrollbarWidth = async (tableDisplay: RenderResult) => {
  const grids = await getInnerGrids(tableDisplay);

  const innerTable = grids[0].children[0].children[0].children[0] as HTMLElement;
  return grids[0].offsetWidth - innerTable.offsetWidth;
};

/**
 * Helper function to apply a longer timeout for tests
 */
export const enableLongTimeout = () => {
  if (typeof jest === 'undefined') {
    mocha.timeout(TABLEDISPLAY_LONG_TIMEOUT);
  } else {
    jest.setTimeout(TABLEDISPLAY_LONG_TIMEOUT);
  }
};
